import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import tenantMediaService from '../../services/tenantMedia.ts';
import './TenantMedia.css';

interface MenuUploaderProps {
  tenantId: number;
  locationId: number; // required for uploads
}

const MenuUploader: React.FC<MenuUploaderProps> = ({ tenantId, locationId }) => {
  const [hasMenu, setHasMenu] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    checkMenuStatus();
  }, [tenantId]);

  const checkMenuStatus = async () => {
    const menuExists = await tenantMediaService.hasMenu(tenantId);
    setHasMenu(menuExists);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'application/pdf') {
      toast.error('Please select a PDF file');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('PDF file size must not exceed 10MB');
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.warning('Please select a PDF file first');
      return;
    }
    if (!locationId) {
      toast.error('Please select a location');
      return;
    }

    setUploading(true);
    try {
      await tenantMediaService.uploadMenu(locationId, selectedFile);
      toast.success('Menu uploaded successfully');
      setHasMenu(true);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to upload menu');
    } finally {
      setUploading(false);
    }
  };

  const openInNewTab = () => {
    window.open(tenantMediaService.getMenuUrl(tenantId), '_blank');
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="menu-uploader">
      <h3 className="section-title">
        <svg className="section-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        Restaurant Menu (PDF)
      </h3>

      <div className="upload-section">
        <div className="file-input-wrapper">
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            onChange={handleFileSelect}
            disabled={uploading}
            className="file-input"
            id="menu-file-input"
          />
          <label htmlFor="menu-file-input" className="file-input-label">
            <svg className="upload-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            {selectedFile ? selectedFile.name : 'Choose PDF file (max 10MB)'}
          </label>
          
          {selectedFile && (
            <div className="file-info">
              <span className="file-size">{formatFileSize(selectedFile.size)}</span>
              <button 
                onClick={() => {
                  setSelectedFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                className="btn-clear"
                type="button"
              >
                ✕
              </button>
            </div>
          )}
        </div>

        <button
          onClick={handleUpload}
          disabled={!selectedFile || uploading}
          className="btn btn-primary"
        >
          {uploading ? (
            <>
              <span className="spinner-small"></span>
              Uploading...
            </>
          ) : (
            <>
              <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              {hasMenu ? 'Replace Menu' : 'Upload Menu'}
            </>
          )}
        </button>
      </div>

      {hasMenu && (
        <div className="menu-preview">
          <div className="preview-header">
            <h4>Current Menu</h4>
            <button onClick={openInNewTab} className="btn btn-secondary">
              <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Open in New Tab
            </button>
          </div>
          
          <div className="pdf-preview">
            <iframe
              src={tenantMediaService.getMenuUrl(tenantId)}
              title="Menu Preview"
              width="100%"
              height="500"
              frameBorder="0"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuUploader;
